import React from 'react';
import {
    Card,
    Typography,
    Button,
    Space,
    Spin,
    Tag,
    Alert,
    Popconfirm,
    List,
    Empty,
    message,
} from 'antd';
import {
    TeamOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    DisconnectOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../hooks/useAuth';
import {
    useGetMyAgencyQuery,
    useAcceptAgencyInvitationMutation,
    useDeclineAgencyInvitationMutation,
    useDisconnectFromAgencyMutation,
} from '../../services/nestjs-base-api';
import { FormattedMessage } from 'react-intl';

const { Title, Text, Paragraph } = Typography;

export const AgencyRelationship: React.FC = () => {
    const { user } = useAuth();
    const { data, isLoading, refetch } = useGetMyAgencyQuery(
        { jwtToken: user?.jwtToken || '' },
        { skip: !user?.jwtToken },
    );
    const [acceptInvitation, { isLoading: isAccepting }] = useAcceptAgencyInvitationMutation();
    const [declineInvitation, { isLoading: isDeclining }] = useDeclineAgencyInvitationMutation();
    const [disconnect, { isLoading: isDisconnecting }] = useDisconnectFromAgencyMutation();

    const handleAccept = async (invitationId: number) => {
        try {
            await acceptInvitation({
                jwtToken: user?.jwtToken || '',
                invitationId,
            }).unwrap();
            message.success('Agency invitation accepted');
            refetch();
        } catch (error: any) {
            message.error(error?.data?.message || 'Failed to accept invitation');
        }
    };

    const handleDecline = async (invitationId: number) => {
        try {
            await declineInvitation({
                jwtToken: user?.jwtToken || '',
                invitationId,
            }).unwrap();
            message.success('Agency invitation declined');
            refetch();
        } catch (error: any) {
            message.error(error?.data?.message || 'Failed to decline invitation');
        }
    };

    const handleDisconnect = async () => {
        try {
            await disconnect({
                jwtToken: user?.jwtToken || '',
            }).unwrap();
            message.success('Disconnected from agency');
            refetch();
        } catch (error: any) {
            message.error(error?.data?.message || 'Failed to disconnect');
        }
    };

    if (isLoading) {
        return (
            <div style={{ textAlign: 'center', padding: 48 }}>
                <Spin size="large" />
            </div>
        );
    }

    const activeAgency = data?.agency;
    const pendingInvitations = data?.pendingInvitations || [];

    return (
        <div>
            {/* Active agency relationship */}
            {activeAgency && (
                <Card style={{ marginBottom: 24 }}>
                    <Space direction="vertical" style={{ width: '100%' }} size="middle">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Space>
                                <TeamOutlined style={{ fontSize: 24, color: '#0369a1' }} />
                                <div>
                                    <Title level={4} style={{ margin: 0 }}>
                                        <FormattedMessage
                                            id="agency.client.managedBy"
                                            defaultMessage="Managed by {agencyName}"
                                            values={{ agencyName: activeAgency.agencyOrganisation?.name || 'Agency' }}
                                        />
                                    </Title>
                                    <Text type="secondary">
                                        <FormattedMessage
                                            id="agency.client.connectedSince"
                                            defaultMessage="Connected since {date}"
                                            values={{
                                                date: activeAgency.acceptedAt
                                                    ? new Date(activeAgency.acceptedAt).toLocaleDateString()
                                                    : '-',
                                            }}
                                        />
                                    </Text>
                                </div>
                            </Space>
                            <Tag color="green">
                                <FormattedMessage id="agency.client.active" defaultMessage="Active" />
                            </Tag>
                        </div>

                        {activeAgency.billingResponsibility === 'AGENCY' && (
                            <Alert
                                type="info"
                                message={
                                    <FormattedMessage
                                        id="agency.client.agencyPaying"
                                        defaultMessage="Your subscription is currently being paid by your agency."
                                    />
                                }
                                showIcon
                            />
                        )}

                        <Paragraph type="secondary">
                            <FormattedMessage
                                id="agency.client.disconnectInfo"
                                defaultMessage="Your agency can view your brand data and help manage your surveys. You can disconnect at any time - your account and data will remain unchanged."
                            />
                        </Paragraph>

                        <Popconfirm
                            title={
                                <FormattedMessage
                                    id="agency.client.disconnectConfirm"
                                    defaultMessage="Are you sure you want to disconnect from this agency? If they are paying for your subscription, you will need to add your own payment method."
                                />
                            }
                            onConfirm={handleDisconnect}
                            okText="Disconnect"
                            cancelText="Cancel"
                            okButtonProps={{ danger: true }}
                        >
                            <Button
                                danger
                                icon={<DisconnectOutlined />}
                                loading={isDisconnecting}
                            >
                                <FormattedMessage
                                    id="agency.client.disconnect"
                                    defaultMessage="Disconnect from Agency"
                                />
                            </Button>
                        </Popconfirm>
                    </Space>
                </Card>
            )}

            {/* Pending invitations */}
            {pendingInvitations.length > 0 && (
                <Card
                    title={
                        <FormattedMessage
                            id="agency.client.pendingInvitations"
                            defaultMessage="Pending Agency Invitations"
                        />
                    }
                    style={{ marginBottom: 24 }}
                >
                    <List
                        dataSource={pendingInvitations}
                        renderItem={(invitation: any) => (
                            <List.Item
                                actions={[
                                    <Button
                                        type="primary"
                                        icon={<CheckCircleOutlined />}
                                        loading={isAccepting}
                                        onClick={() => handleAccept(invitation.id)}
                                        key="accept"
                                    >
                                        <FormattedMessage id="agency.client.accept" defaultMessage="Accept" />
                                    </Button>,
                                    <Button
                                        icon={<CloseCircleOutlined />}
                                        loading={isDeclining}
                                        onClick={() => handleDecline(invitation.id)}
                                        key="decline"
                                    >
                                        <FormattedMessage id="agency.client.decline" defaultMessage="Decline" />
                                    </Button>,
                                ]}
                            >
                                <List.Item.Meta
                                    avatar={<TeamOutlined style={{ fontSize: 20, color: '#0369a1' }} />}
                                    title={invitation.agencyOrganisation?.name || 'Agency'}
                                    description={
                                        <FormattedMessage
                                            id="agency.client.invitedOn"
                                            defaultMessage="Invited on {date}"
                                            values={{
                                                date: invitation.invitedAt
                                                    ? new Date(invitation.invitedAt).toLocaleDateString()
                                                    : '-',
                                            }}
                                        />
                                    }
                                />
                            </List.Item>
                        )}
                    />
                </Card>
            )}

            {/* No agency relationship */}
            {!activeAgency && pendingInvitations.length === 0 && (
                <Empty
                    image={<TeamOutlined style={{ fontSize: 48, color: '#d9d9d9' }} />}
                    description={
                        <FormattedMessage
                            id="agency.client.noAgency"
                            defaultMessage="Your organisation is not connected to any agency. If your agency has sent you an invitation, it will appear here."
                        />
                    }
                />
            )}
        </div>
    );
};
